import React, { useState } from "react";
import styled from "@emotion/styled";
import { Empty } from "antd5";
import { Flex, Text } from "styles/utility-components";

import CreateBidModal from "components/homepage/CreateBidModal";
import { CreateOpportunityDrawer } from "components/opportunities/create_opportunity/CreateOpportunityDrawer";
import { CreditAccessWrapper } from "components/paywall/CreditAccessWrapper";
import Button from "components/ui/button/Button";
import { EllipsisTooltipTextLink } from "lib/core_components/EllipsisTooltip";
import { ColumnType, commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { Table } from "lib/core_components/Table/Table";
import { BidsDto, useBids } from "lib/hooks/api/bid/bids/useBids";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { formatDate } from "lib/utils";

import css from "./MyBidsTable.module.scss";

const columns: ColumnType<BidsDto>[] = [
  {
    ...commonTableColumns.titleColumn,
    key: "name",
    title: "Opportunity title",
    render: (_t: unknown, bid: BidsDto) => (
      <EllipsisTooltipTextLink
        fullText={bid.opportunity.name}
        containerClassname={css.linkTitle}
        linkText={bid.opportunity.name}
        linkProps={{ to: `/opportunities/${bid.opportunity.id}/bid_qualification/${bid.id}` }}
      />
    ),
  },
  {
    ...commonTableColumns.dateColumn,
    key: "created_at",
    dataIndex: "createdAt",
    title: "Creation date",
    render: (_, b) => (b.createdAt ? formatDate(new Date(b.createdAt)) : b.createdAt),
  },
];
export function MyBidsTable() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);

  const [pageSize, setPageSize] = useTablePageSize("my-bids-table");
  const [currentPage, setCurrentPage] = useState(1);

  const handleDrawerClose = () => {
    setIsDrawerClosing(true);
    setTimeout(() => {
      setIsDrawerOpen(false);
      setIsDrawerClosing(false);
    }, 300); // timeout duration to match the drawer closing animation duration
  };
  const [bidModalOpen, setBidModalOpen] = useState(false);

  const {
    data: bids,
    isLoading,
    isError,
  } = useBids(
    {}, // Empty request fetches all bids
  );

  return (
    <Flex column gap={8}>
      {bids && bids.totalResults > 0 && (
        <Text color="sysTextSecondary">
          {bids.totalResults} results • Sorted by most recently created
        </Text>
      )}
      <Table<BidsDto>
        dataSource={bids?.results || []}
        loading={isLoading}
        columns={columns}
        isError={isError}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize,
          onChange: (page, pageSize) => {
            setPageSize(pageSize);
            setCurrentPage(page);
          },
        }}
        locale={{
          emptyText: (
            <EmptyStateContainer
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Flex flexDirection="column" gap={16} alignItems="center">
                  <Text>You have created no bids yet. Click the button below to get started!</Text>
                  <CreditAccessWrapper
                    requiredDataType="BIDS"
                    creditType="BID"
                    contextSource="Bid studio"
                  >
                    <Button style={{ width: "fit-content" }} onClick={() => setBidModalOpen(true)}>
                      Win bids
                    </Button>
                  </CreditAccessWrapper>
                </Flex>
              }
            />
          ),
        }}
      />
      <CreateBidModal
        open={bidModalOpen}
        onOpenChange={(open) => setBidModalOpen(open)}
        openOpportunityDrawer={() => {
          setIsDrawerOpen(true);
          setBidModalOpen(false);
        }}
      />
      {(isDrawerOpen || isDrawerClosing) && (
        <CreateOpportunityDrawer
          isOpen={isDrawerOpen && !isDrawerClosing}
          onClose={handleDrawerClose}
          hiddenFields={["value", "closeDate"]}
          defaultValues={{
            stage: "OPEN",
            noticeStage: "Tender",
          }}
          navigateToNewOpportunity
        />
      )}
    </Flex>
  );
}
const EmptyStateContainer = styled(Empty)({
  margin: "24px 0",
});
